/**
 * AttendanceRow.jsx — Tek puantaj satırı
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertTriangle, XCircle, FileText } from 'lucide-react';
import { getStatusBadgeClass, getApprovalBadgeClass, decimalToDisplay } from '../utils/attendanceHelpers';
import EmployeeAvatar from '../../../components/common/EmployeeAvatar';

const statusIconMap = {
  PRESENT: CheckCircle,
  LATE: AlertTriangle,
  ABSENT: XCircle,
  SICK_REPORT: FileText,
  EARLY_OUT: AlertTriangle,
  LATE_EARLY_OUT: AlertTriangle,
  MISSING_CLOCK_IN: XCircle,
  MISSING_CLOCK_OUT: XCircle,
};

const getStatusLabel = (status, t) => {
  const map = {
    PRESENT: t('status_present', 'Mevcut'),
    LATE: t('status_late', 'Geç Kaldı'),
    ABSENT: t('status_absent', 'Devamsız'),
    SICK_REPORT: t('status_sick_report', 'Sağlık Raporu'),
    EARLY_OUT: t('status_early_out', 'Erken Çıkış'),
    LATE_EARLY_OUT: t('status_late_early', 'Geç + Erken'),
    MISSING_CLOCK_IN: t('violation_missing_in', 'Eksik Giriş'),
    MISSING_CLOCK_OUT: t('violation_missing_out', 'Eksik Çıkış'),
  };
  return map[status] || status || '-';
};

const getApprovalLabel = (approval, t) => {
  const map = {
    PENDING: t('badge_manager_pending', 'Müdür Onayı Bekliyor'),
    MANAGER_APPROVED: t('badge_hr_pending', 'İK Onayı Bekliyor'),
    HR_APPROVED: t('badge_payroll_ready', 'Bordroya Hazır'),
    REJECTED: t('badge_rejected', 'Reddedildi'),
  };
  return map[approval] || approval || '-';
};

const AttendanceRow = ({
  record,
  isSelected = false,
  onSelect,
  showEmployee = true,
  showApproval = false,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const statusIcon = statusIconMap[record.status] || FileText;
  const Icon = statusIcon;

  return (
    <tr className={`border-b border-slate-50 transition-colors hover:bg-slate-50/50 ${isSelected ? 'bg-cyan-50/30' : ''}`}>
      {/* Checkbox (toplu seçim için) */}
      {onSelect && (
        <td className="pl-4 py-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(record.id)}
            className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
          />
        </td>
      )}

      {/* Personel bilgisi */}
      {showEmployee && (
        <td className="px-3 py-3">
          <div className="flex items-center gap-2.5">
            <EmployeeAvatar
              firstName={record.employee_first_name || record.first_name}
              lastName={record.employee_last_name || record.last_name}
              size={32}
            />
            <div>
              <p className="text-sm font-bold text-slate-800">
                {record.employee_first_name || record.first_name} {record.employee_last_name || record.last_name}
              </p>
              {record.department_name && (
                <p className="text-[10px] text-slate-400 font-medium">{record.department_name}</p>
              )}
            </div>
          </div>
        </td>
      )}

      {/* Tarih */}
      <td className="px-3 py-3 text-sm font-medium text-slate-600">
        {record.date ? new Date(record.date).toLocaleDateString('tr-TR') : '-'}
      </td>

      {/* Giriş saati */}
      <td className="px-3 py-3 text-sm font-medium text-slate-700">
        {record.clock_in || '-'}
      </td>

      {/* Çıkış saati */}
      <td className="px-3 py-3 text-sm font-medium text-slate-700">
        {record.clock_out || '-'}
      </td>

      {/* Toplam süre */}
      <td className="px-3 py-3">
        <span className="text-sm font-bold text-slate-800">
          {decimalToDisplay(record.total_work_hours)}
        </span>
      </td>

      {/* Durum badge */}
      <td className="px-3 py-3">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide ${getStatusBadgeClass(record.status)}`}>
          <Icon size={10} />
          {getStatusLabel(record.status, t)}
        </span>
      </td>

      {/* Onay durumu */}
      {showApproval && (
        <td className="px-3 py-3">
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide ${getApprovalBadgeClass(record.approval || record.approval_status)}`}>
            {getApprovalLabel(record.approval || record.approval_status, t)}
          </span>
        </td>
      )}

      {/* Aksiyon butonları */}
      {(onApprove || onReject) && (
        <td className="px-3 py-3">
          <div className="flex items-center gap-1.5">
            {onApprove && (
              <button
                onClick={() => onApprove(record.id)}
                className="rounded-lg bg-emerald-50 p-1.5 text-emerald-600 hover:bg-emerald-100 transition-colors"
                title={t('approve', 'Onayla')}
              >
                <CheckCircle size={14} />
              </button>
            )}
            {onReject && (
              <button
                onClick={() => onReject(record.id)}
                className="rounded-lg bg-rose-50 p-1.5 text-rose-600 hover:bg-rose-100 transition-colors"
                title={t('reject', 'Reddet')}
              >
                <XCircle size={14} />
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

export default AttendanceRow;